import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Shield, Zap } from "lucide-react";

import Header from "../components/layout/Header";
import SiteFooter from "../components/layout/SiteFooter";
import { useAuth } from "../hooks/useAuth";


const PLANS = [
  {
    id: "basic",
    name: "Basic",
    sub: "Perfect for getting started",
    monthly: 9,
    yearly: 90,
    features: [
      "100 song generations per month",
      "Standard genres & moods",
      "MP3 downloads, royalty-free",
      "Email support",
    ],
    cta: "Start with Basic",
  },
  {
    id: "pro",
    name: "Pro",
    sub: "For creators who ship daily",
    monthly: 19,
    yearly: 190,
    featured: true,
    features: [
      "Unlimited song generations",
      "All genres, moods & advanced styles",
      "Music videos & AI voice covers",
      "Studio-quality MP3 + priority rendering",
      "Priority support",
    ],
    cta: "Go Pro",
  },
];


export default function PricingPage() {
  const { isAuthed, user, clearAuth } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState("monthly");

  const monthlyRef = useRef(null);
  const yearlyRef = useRef(null);
  const [indicator, setIndicator] = useState({ x: 0, w: 0 });

  useLayoutEffect(() => {
    const el = billing === "monthly" ? monthlyRef.current : yearlyRef.current;
    if (el) {
      const parentLeft = el.parentElement.getBoundingClientRect().left;
      const r = el.getBoundingClientRect();
      setIndicator({ x: r.left - parentLeft, w: r.width });
    }
  }, [billing]);

  function subscribe(plan) {
    if (isAuthed) {
      navigate(`/app?subscribe=${plan}&billing=${billing}`, { replace: false });
    } else {
      // Send to landing; user signs in then returns
      navigate("/", { replace: false });
    }
  }

  const yearlySavings = useMemo(() => {
    return PLANS.map((p) => ({
      id: p.id,
      save: Math.max(0, p.monthly * 12 - p.yearly),
    })).reduce((acc, x) => ({ ...acc, [x.id]: x.save }), {});
  }, []);

  return (
    <div className="app-shell v2-page">
      <div className="v2-page-bg" aria-hidden="true">
        <div className="v2-hero-glow v2-glow-1" />
        <div className="v2-hero-glow v2-glow-2" />
      </div>
      <Header isAuthed={isAuthed} user={user} onLogout={clearAuth} onOpenAccount={() => navigate("/app")} />

      <main className="v2-main">
        <section className="v2-page-hero">
          <div className="v2-container">
            <span className="v2-pill"><Sparkles size={14} /> Simple pricing</span>
            <h1 className="v2-page-title">
              Start free. <span className="v2-gradient-text">Upgrade when you're ready.</span>
            </h1>
            <p className="v2-page-lead">
              Pick a plan that fits how you create. Cancel anytime. 30-day money-back guarantee.
            </p>

            <div className="v2-billing-toggle" role="tablist" aria-label="Billing period">
              <div className="v2-billing-indicator" style={{ transform: `translateX(${indicator.x}px)`, width: `${indicator.w}px` }} />
              <button
                ref={monthlyRef}
                role="tab"
                aria-selected={billing === "monthly"}
                className={`v2-billing-btn ${billing === "monthly" ? "is-active" : ""}`}
                onClick={() => setBilling("monthly")}
              >
                Monthly
              </button>
              <button
                ref={yearlyRef}
                role="tab"
                aria-selected={billing === "yearly"}
                className={`v2-billing-btn ${billing === "yearly" ? "is-active" : ""}`}
                onClick={() => setBilling("yearly")}
              >
                Yearly<span className="v2-yearly-chip">Save 17%</span>
              </button>
            </div>
          </div>
        </section>

        <section className="v2-page-body">
          <div className="v2-container">
            <div className="v2-pricing-grid">
              {PLANS.map((p) => {
                const price = billing === "monthly" ? p.monthly : p.yearly;
                const period = billing === "monthly" ? "/mo" : "/yr";
                const save = yearlySavings[p.id];
                return (
                  <div key={p.id} className={`v2-price-card ${p.featured ? "is-featured" : ""}`}>
                    {p.featured && (
                      <div className="v2-price-badge"><Sparkles size={11} /> MOST POPULAR</div>
                    )}
                    <div className="v2-price-title">{p.name}</div>
                    <div className="v2-price-sub">{p.sub}</div>

                    <div className="v2-price-amount-row">
                      <div className="v2-price-amount">
                        <span className="v2-amount-currency">$</span>{price}
                      </div>
                      <div className="v2-price-period">{period}</div>
                    </div>
                    <div className={`v2-price-savings ${billing === "yearly" && save > 0 ? "" : "is-hidden"}`}>
                      {billing === "yearly" && save > 0 ? `Save $${save} per year` : "placeholder"}
                    </div>

                    <ul className="v2-price-list">
                      {p.features.map((f, i) => (
                        <li key={i}><Check size={16} /> {f}</li>
                      ))}
                    </ul>

                    <button className="v2-price-cta" onClick={() => subscribe(p.id)}>
                      {p.cta} <ArrowRight size={16} />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="v2-guarantee-wrap">
              <span className="v2-guarantee">
                <Shield size={14} /> 30-day money-back guarantee
                <span style={{ opacity: 0.4, margin: "0 8px" }}>·</span>
                <Zap size={14} /> Cancel anytime
              </span>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
